import Lightbox from "./lightbox";
import { initCarousel } from "./carousel";
import { createFocusTrap } from "../utils/focus-trap";
import { applyReducedMotionVideos } from "../utils/reduced-motion";
import { initLazyMedia } from "../utils/lazy-media";
import { initCollapsibleText } from "./collapsible-text";
import { projectsIndexUrl } from "../utils/language-url";

const PROJECT_LINK_SELECTOR =
  "[data-color-combination][href].c-projects-list__item-link, " +
  "[data-color-combination][href].c-home-slideshow__link, " +
  "[data-color-combination][href].c-projects-selected__link, " +
  "[data-color-combination][href].c-projects-index__link, " +
  "[data-color-combination][href].c-projects-index-table__link";

export default class ProjectModal {
  constructor() {
    this.modal = null;
    this.closeButton = null;
    this.isOpen = false;
    this.lastFocusedElement = null;
    this.focusTrap = null;
    this.colorCache = new Map();
    this.init();
  }

  init() {
    this.createModal();
    this.attachEventListeners();
  }

  createModal() {
    const closeLabel = document.body.dataset.labelClose || "Close";
    const homeUrl = document.body.dataset.homeUrl || "/";
    const modalHTML = `
      <div class="c-project-modal" id="project-modal" role="dialog" aria-modal="true" aria-labelledby="project-modal-title" aria-hidden="true" inert>
        <header class="c-project-modal__chrome">
          <a href="${homeUrl}" class="c-project-modal__brand">Modulo Architects</a>
          <h1 class="c-project-modal__title js-project-modal-title" id="project-modal-title"></h1>
          <button type="button" class="c-project-modal__close js-project-modal-close" aria-label="${closeLabel}"></button>
        </header>
        <div class="c-project-modal__container">
          <div class="c-project-modal__content js-project-modal-content">
            <!-- Content will be loaded here -->
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML("beforeend", modalHTML);
    this.modal = document.getElementById("project-modal");
    this.closeButton = document.querySelector(".js-project-modal-close");
    this.focusTrap = createFocusTrap(this.modal);
  }

  softClose() {
    if (!this.modal) return;

    this.modal.classList.remove("is-active");
    this.modal.setAttribute("aria-hidden", "true");
    this.modal.inert = true;
    this.isOpen = false;
    document.body.classList.remove("no-scroll");
    this.focusTrap?.deactivate();
    this.resetColorCombination();
    this.lastFocusedElement = null;

    // Tear down any lightbox left open from modal media.
    document.querySelectorAll(".js-gallery").forEach((node) => {
      node.classList.remove("is-active");
      node.setAttribute("aria-hidden", "true");
      node.remove();
    });
    document.body.style.overflow = "";
  }

  attachEventListeners() {
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.close());
    }

    if (this.modal) {
      const brandLink = this.modal.querySelector("a.c-project-modal__brand");
      if (brandLink) {
        brandLink.addEventListener("click", () => {
          // Soft-close so Swup/home nav is not overwritten by close()'s projects URL push
          this.softClose();
        });
      }

      this.modal.addEventListener("click", (e) => {
        if (
          e.target === this.modal ||
          e.target.classList.contains("c-project-modal__container")
        ) {
          this.close();
        }
      });
    }

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    if (this.modal) {
      this.modal.addEventListener("click", (e) => {
        const relatedLink = e.target.closest(
          "a.c-project-related__card, a.c-projects-index-table__link, a.c-projects-index__link, a.c-projects-list__item-link"
        );
        if (!relatedLink || !this.modal.contains(relatedLink)) return;
        e.preventDefault();
        const url = relatedLink.getAttribute("href");
        if (url) {
          this.navigate(url, relatedLink);
        }
      });
    }
  }

  normalizeProjectUrl(url) {
    try {
      return new URL(url, window.location.origin).href;
    } catch (error) {
      return url;
    }
  }

  readColorFromElement(element) {
    if (!element) {
      return null;
    }

    const key = element.getAttribute("data-color-combination");
    if (!key) {
      return null;
    }

    return {
      key,
      accent: element.getAttribute("data-project-accent") || "",
      accentText: element.getAttribute("data-project-accent-text") || "",
    };
  }

  findProjectLink(url) {
    const normalized = this.normalizeProjectUrl(url);

    return (
      Array.from(document.querySelectorAll(PROJECT_LINK_SELECTOR)).find(
        (link) => this.normalizeProjectUrl(link.href) === normalized
      ) || null
    );
  }

  resolveColor(url, sourceElement = null, colorHint = null) {
    if (colorHint?.key) {
      return colorHint;
    }

    const fromSource =
      this.readColorFromElement(sourceElement) ||
      this.readColorFromElement(this.findProjectLink(url));

    if (fromSource) {
      return fromSource;
    }

    return this.colorCache.get(this.normalizeProjectUrl(url)) || null;
  }

  cacheColor(url, color) {
    if (!color?.key) {
      return;
    }

    this.colorCache.set(this.normalizeProjectUrl(url), color);
  }

  extractColorCombination(doc) {
    const projectRoot = doc.querySelector(
      ".c-project-page[data-color-combination], .c-project-modal[data-color-combination]"
    );
    if (projectRoot) {
      return {
        key: projectRoot.getAttribute("data-color-combination") || "black-white",
        accent: projectRoot.style.getPropertyValue("--project-accent").trim() || "",
        accentText:
          projectRoot.style.getPropertyValue("--project-accent-text").trim() || "",
      };
    }

    const bodyClass = doc.body?.className || "";
    const match = bodyClass.match(/page-color-combo-([\w-]+)/);
    return {
      key: match ? match[1] : "black-white",
      accent: "",
      accentText: "",
    };
  }

  applyColorCombination({ key, accent, accentText }) {
    if (!this.modal) return;

    Array.from(this.modal.classList).forEach((className) => {
      if (className.startsWith("page-color-combo-")) {
        this.modal.classList.remove(className);
      }
    });

    if (key) {
      this.modal.classList.add(`page-color-combo-${key}`);
      this.modal.setAttribute("data-color-combination", key);
    }

    if (accent) {
      this.modal.style.setProperty("--project-accent", accent);
    } else {
      this.modal.style.removeProperty("--project-accent");
    }

    if (accentText) {
      this.modal.style.setProperty("--project-accent-text", accentText);
    } else {
      this.modal.style.removeProperty("--project-accent-text");
    }
  }

  resetColorCombination() {
    this.applyColorCombination({
      key: "black-white",
      accent: "",
      accentText: "",
    });
  }

  prepareForLoad(url, sourceElement = null, colorHint = null) {
    const color = this.resolveColor(url, sourceElement, colorHint);

    if (color) {
      this.applyColorCombination(color);
    } else {
      this.resetColorCombination();
    }

    const titleElement = this.modal?.querySelector(".js-project-modal-title");
    if (titleElement) {
      titleElement.textContent = "";
    }
  }

  async loadProject(url) {
    try {
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      const titleElement =
        doc.querySelector(".c-project-page__title") ||
        doc.querySelector(".c-project-modal__title") ||
        doc.querySelector("h1.u-sr-only") ||
        doc.querySelector("h1");
      const title = titleElement?.textContent?.trim() || "";

      const projectContent = doc.querySelector(".c-project-content");
      const contentHTML = projectContent
        ? projectContent.innerHTML
        : doc.querySelector("main")?.innerHTML || "";

      const color = this.extractColorCombination(doc);

      return {
        title,
        content: contentHTML,
        color,
      };
    } catch (error) {
      console.error("Error loading project:", error);
      return {
        title: "Error",
        content:
          '<div style="padding: 2rem;"><p>Error loading project content.</p></div>',
        color: { key: "black-white", accent: "", accentText: "" },
      };
    }
  }

  async open(url, sourceElement = null, colorHint = null) {
    if (!this.modal) return;

    this.lastFocusedElement = document.activeElement;
    this.prepareForLoad(url, sourceElement, colorHint);
    this.modal.inert = false;
    this.modal.classList.add("is-active");
    this.modal.setAttribute("aria-hidden", "false");
    this.isOpen = true;
    document.body.classList.add("no-scroll");
    await this.replaceContent(url, sourceElement, colorHint);
    this.focusTrap.activate();
    if (this.closeButton) {
      this.closeButton.focus();
    }
  }

  async navigate(url, sourceElement = null) {
    if (!this.modal || !this.isOpen) return;
    await this.replaceContent(url, sourceElement);
  }

  async replaceContent(url, sourceElement = null, colorHint = null) {
    const contentContainer = this.modal.querySelector(".js-project-modal-content");
    const titleElement = this.modal.querySelector(".js-project-modal-title");

    if (contentContainer) {
      this.prepareForLoad(url, sourceElement, colorHint);
      contentContainer.classList.add("is-loading");
      const loadingLabel = document.body.dataset.labelLoading || "Loading…";
      contentContainer.innerHTML = `<div style="text-align: center; padding: 2rem;">${loadingLabel}</div>`;

      const { title, content, color } = await this.loadProject(url);

      this.applyColorCombination(color);
      this.cacheColor(url, color);

      if (titleElement && title) {
        titleElement.textContent = title.toUpperCase();
      }

      contentContainer.innerHTML = `<div class="c-project-content">${content}</div>`;

      this.reinitScripts();
      this.modal.scrollTop = 0;

      window.requestAnimationFrame(() => {
        contentContainer.classList.remove("is-loading");
      });
    }

    if (history.pushState) {
      history.pushState({ modal: true }, "", url);
    }
  }

  close() {
    if (!this.modal) return;

    const restoreFocus = this.lastFocusedElement;
    this.softClose();

    if (restoreFocus && typeof restoreFocus.focus === "function") {
      restoreFocus.focus();
    }

    if (history.pushState && window.location.pathname.includes("/projects/")) {
      history.pushState({}, "", projectsIndexUrl());
    }
  }

  reinitScripts() {
    initLazyMedia(this.modal);

    initCollapsibleText(this.modal);

    const lightboxLinks = this.modal.querySelectorAll('[data-rel="aiLightbox"]');
    if (lightboxLinks.length > 0) {
      new Lightbox({
        // Scope to modal only — page-level lightboxes are separate instances
        selector: '.c-project-modal a[data-rel="aiLightbox"]',
        lazyload: true,
        arrows: true,
        counter: true,
        slideSpeed: 500,
      });
    }

    initCarousel();
    applyReducedMotionVideos(this.modal);
  }

  destroy() {
    if (this.modal) {
      this.modal.remove();
    }
  }
}
